import { Checkbox, FormControlLabel, Grid, Stack, TextField, debounce } from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";

import { UserContextProps } from "../../../../../Context/UserContext";
import { CustomDimension } from "../../../../../Models/CustomDimension";
import { NotOfferedProcedure } from "../../../../../Utils/Enums";
import NotOfferedProcedureOptions from "../../Types/NotOfferedProcedureOptions";
import { BaseDispositionConfirmationForm, IBaseDispositionConfirmationFormProps } from "./ConfirmationForm";

export const classNames = {
  notOfferedProcedureOption: "dispositionModalNotOfferedProcedureOption",
  notOfferedProcedureNote: "dispositionModalNotOfferedProcedureNote"
};

export interface IOtherProceduresConfirmationFormProps extends IBaseDispositionConfirmationFormProps {
  /** The available 'not offered procedure' options which the user can elect for the lead result. */
  notOfferedProceduresOptions: UserContextProps["notOfferedProcedures"];
}

/** An extension of the confirmation form to be used when the disposition type is 'Other Procedures'. */
const OtherProceduresConfirmationForm: React.FC<IOtherProceduresConfirmationFormProps> = ({
  leadResultProps,
  notOfferedProceduresOptions,
  setLeadResultProps,
  ...rest
}) => {
  const [options, setNotOfferedProcedureProps] = useState<NotOfferedProcedureOptions>(
    NotOfferedProcedureOptions.fromLeadResult(leadResultProps.notOfferedProcedures || {}, notOfferedProceduresOptions)
  );
  const [notes, setNotes] = useState(leadResultProps.notes || "");
  const leadResultPropsRef = useRef(leadResultProps);

  useEffect(() => {
    leadResultPropsRef.current = leadResultProps;
  }, [leadResultProps]);
  useEffect(() => setNotes(leadResultProps.notes || ""), [leadResultProps.notes]);
  useEffect(
    () =>
      setNotOfferedProcedureProps(
        NotOfferedProcedureOptions.fromLeadResult(
          leadResultProps.notOfferedProcedures || {},
          notOfferedProceduresOptions
        )
      ),
    [leadResultProps.notOfferedProcedures]
  );
  const debouncedNotesChange = useCallback(
    debounce((notes: string) => setLeadResultProps({ ...leadResultPropsRef.current, notes: notes || null })),
    []
  );
  const handleNotesChange = (notes: string) => {
    setNotes(notes);
    debouncedNotesChange(notes);
  };

  const notOfferedProceduresSelections = ` with ${
    options.state.filter(({ value }) => value).reduce((str, { name }) => (str ? `${str}, ${name}` : name), "") ||
    "nothing"
  } selected`;

  const fieldDisabled = !options.state.some(o => o.name === NotOfferedProcedure.Other && o.value);

  return (
    <BaseDispositionConfirmationForm
      {...rest}
      leadResultProps={leadResultProps}
      setLeadResultProps={setLeadResultProps}
      summaryAddendum={notOfferedProceduresSelections}
    >
      <Stack spacing={2}>
        <Grid container>
          {options.state.sort(CustomDimension.NameComparator).map((option, index) => (
            <Grid item key={index}>
              <FormControlLabel
                className={classNames.notOfferedProcedureOption}
                control={<Checkbox color="primary" />}
                label={option.name}
                labelPlacement="start"
                onChange={() =>
                  setLeadResultProps({
                    ...leadResultProps,
                    notOfferedProcedures: options.toggleOption(option).getForLeadResult()
                  })
                }
                checked={option.value}
              />
            </Grid>
          ))}
        </Grid>
        <TextField
          className={classNames.notOfferedProcedureNote}
          label="Other"
          onChange={({ target: { value } }) => handleNotesChange(value)}
          InputLabelProps={{ shrink: true }}
          value={notes}
          disabled={fieldDisabled}
          type="string"
        />
      </Stack>
    </BaseDispositionConfirmationForm>
  );
};

export default OtherProceduresConfirmationForm;
